import { useState, useEffect } from 'react';
import { MapPin, User, Zap, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveTracker = ({ bookingId }) => {
  const [booking, setBooking] = useState(null);
  const [barber, setBarber] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('pending');
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
      const interval = setInterval(fetchBookingDetails, 5000); // Update every 5s
      return () => clearInterval(interval);
    }
  }, [bookingId]);

  useEffect(() => {
    simulateProgress();
  }, [status]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      setStatus(response.data.status);

      if (response.data.barber_id) {
        const barberResponse = await axios.get(`${API}/barbers/${response.data.barber_id}`);
        setBarber(barberResponse.data);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  const simulateProgress = () => {
    switch(status) {
      case 'pending':
        setProgress(25);
        setEta('Finding your barber...');
        break;
      case 'confirmed':
        setProgress(50);
        setEta('Barber assigned! ETA: 15 mins');
        break;
      case 'in_progress':
        setProgress(75);
        setEta('Barber on the way! ETA: 5 mins');
        break;
      case 'completed':
        setProgress(100);
        setEta('Service completed!');
        break;
      default:
        setProgress(0);
    }
  };

  const getStatusColor = () => {
    switch(status) {
      case 'pending': return 'text-yellow-400';
      case 'confirmed': return 'text-blue-400';
      case 'in_progress': return 'text-green-400';
      case 'completed': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  if (!booking) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl shadow-2xl p-6 z-50 animate-slide-in" data-testid="live-tracker">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
          <span className="font-bold text-[var(--text-primary)]">Live Tracking</span>
        </div>
        <span className={`text-sm font-semibold ${getStatusColor()}`}>
          {status.toUpperCase()}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="h-2 bg-[var(--bg-primary)] dark:bg-[#111111] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#00C167] to-[#009E54] transition-all duration-1000 ease-out"
            style={{ width: `${progress}%` }}
          >
            <div className="h-full w-full animate-shimmer" style={{
              backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              backgroundSize: '200% 100%'
            }}></div>
          </div>
        </div>
        <div className="text-center mt-2 text-sm text-[var(--text-secondary)]">
          {eta}
        </div>
      </div>

      {/* Barber Info */}
      {barber && (
        <div className="bg-[var(--bg-primary)] dark:bg-[#111111] rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#00C167]/20 flex items-center justify-center">
              <User className="w-6 h-6 text-[#00C167]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[var(--text-primary)]">{barber.full_name}</div>
              <div className="text-sm text-[var(--text-secondary)] flex items-center gap-1">
                <span>⭐ {barber.rating.toFixed(1)}</span>
                <span>•</span>
                <span>{barber.experience_years} yrs exp</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-[var(--text-secondary)]">Specialties</div>
              <div className="text-sm font-medium text-[var(--text-primary)]">
                {barber.specialties.slice(0, 2).join(', ')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Details */}
      <div className="space-y-3">
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="w-4 h-4 text-[#00C167] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-xs text-[var(--text-secondary)] mb-1">Location</div>
            <div className="text-[var(--text-primary)]">{booking.address}</div>
          </div>
        </div>
        
        <div className="flex items-start gap-2 text-sm">
          <Clock className="w-4 h-4 text-[#00C167] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-xs text-[var(--text-secondary)] mb-1">Scheduled</div>
            <div className="text-[var(--text-primary)]">{booking.booking_date} at {booking.booking_time}</div>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm">
          <Zap className="w-4 h-4 text-[#00C167] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-xs text-[var(--text-secondary)] mb-1">Service</div>
            <div className="text-[var(--text-primary)]">{booking.service_name}</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default LiveTracker;